import xhr from 'xhr';

/**
 * Cliente API de la sección "Rescate de Videos" en el admin.
 * Llama a klazia-api (Strapi) — /api/rescue/* — que a su vez consulta la BD
 * y proxeará al klazia-worker cuando se encolen batches (en la Fase 10.3).
 */

/** GET /api/rescue/summary — KPIs globales. */
export async function getRescueSummary() {
	const res = await xhr.get('/rescue/summary');
	return res.data;
}

/**
 * GET /api/rescue/programs — lista base de programas (sin counts de BD).
 * Filtra por tipo si se pasa `type` — más rápido cuando solo interesa
 * bachillerato/técnica/curso.
 */
export async function listRescuePrograms({ type } = {}) {
	const params = type ? { type } : undefined;
	const res = await xhr.get('/rescue/programs', { params });
	return res.data;
}

/**
 * POST /api/rescue/programs/status — counts on-demand del programa concreto.
 * Se dispara al click "Consultar" en la fila para no cargar la BD al abrir.
 */
export async function getProgramStatus(key) {
	const res = await xhr.post('/rescue/programs/status', { key });
	return res.data;
}

/** GET /api/rescue/hypershared — 29 videos hipercompartidos + estado. */
export async function listHypershared() {
	const res = await xhr.get('/rescue/hypershared');
	return res.data;
}

/**
 * POST /api/rescue/programs/queue-batch — encola un batch de restauración al
 * klazia-worker. klazia-api filtra automáticamente los materials ya
 * restaurados y los hipercompartidos.
 */
export async function queueBatchForProgram({ key, size, voicePreset }) {
	const res = await xhr.post('/rescue/programs/queue-batch', {
		key,
		size,
		voice_preset: voicePreset || undefined,
	});
	return res.data;
}

/**
 * POST /api/rescue/hypershared/queue-batch — encola un batch prioritario con
 * los N primeros videos hipercompartidos pendientes. Voz por defecto: bilingue
 * (son mayoritariamente inglés).
 */
export async function queueHypersharedBatch({ size, voicePreset }) {
	const res = await xhr.post('/rescue/hypershared/queue-batch', {
		size,
		voice_preset: voicePreset || undefined,
	});
	return res.data;
}

/** GET /api/rescue/history — lista de batches con al menos 1 callback recibido. */
export async function listRescueHistory({ limit = 50 } = {}) {
	const res = await xhr.get('/rescue/history', { params: { limit } });
	return res.data;
}

/** GET /api/rescue/history/daily-progress — buckets diarios de completados/fallidos. */
export async function getDailyProgress({ days = 30 } = {}) {
	const res = await xhr.get('/rescue/history/daily-progress', { params: { days } });
	return res.data;
}

/**
 * GET /api/rescue/batches/:batchId/live-status — estado en vivo desde el worker
 * (proxy). Se usa para el chip de "Batch en curso" mientras se procesa.
 */
export async function getBatchLiveStatus(batchId) {
	const res = await xhr.get(`/rescue/batches/${encodeURIComponent(batchId)}/live-status`);
	return res.data;
}

/** GET /api/rescue/history/:batchId/items — items detallados del batch. */
export async function getBatchItems(batchId) {
	const res = await xhr.get(`/rescue/history/${encodeURIComponent(batchId)}/items`);
	return res.data;
}

/**
 * POST /api/rescue/history/:batchId/retry-failed — reintenta los items
 * fallidos del batch (nuevo batch_id con sufijo -retry-XXXX).
 */
export async function retryFailedBatch(batchId, { voicePreset } = {}) {
	const res = await xhr.post(`/rescue/history/${encodeURIComponent(batchId)}/retry-failed`, {
		voice_preset: voicePreset || undefined,
	});
	return res.data;
}

/**
 * POST /api/rescue/history/:batchId/items/:materialId/regenerate — regenera
 * un video individual del batch con instrucciones libres para el LLM
 * (nuevo batch_id con sufijo -regen-<materialId>-XXXX). Sobrescribe el video
 * en su path original — no hay rollback.
 */
export async function regenerateBatchItem(
	batchId,
	materialId,
	{ customInstruction, voicePreset, llmModel } = {},
) {
	const res = await xhr.post(
		`/rescue/history/${encodeURIComponent(batchId)}/items/${encodeURIComponent(materialId)}/regenerate`,
		{
			custom_instruction: customInstruction,
			voice_preset: voicePreset || undefined,
			llm_model: llmModel || undefined,
		},
	);
	return res.data;
}
